import { ManagementCompany, Role, User } from "@/models";
import { Permission } from "@/models/Permission";
import { PlatformRoleAssignment, ManagementCompanyRoleAssignment, AssociationRoleAssignment } from "@/models/RoleAssignment";
import { apiRequest, SUPABASE_URL } from "./apiUtils";
import { createApiWrapper } from "./apiWrapper";

// Create reusable API wrappers
export interface OperatingHoursDay {
  day_of_week: number; // 0 = Sunday
  is_open: boolean;
  opens_at: string | null;  // HH:MM
  closes_at: string | null; // HH:MM — doubles as the order cutoff
}

export interface Closure {
  id?: number;
  label: string;
  closure_date?: string | null;      // a specific day
  recurring_month?: number | null;   // or the same day every year
  recurring_day?: number | null;
  source?: 'manual' | 'federal' | 'state';
  /** Set when a generated holiday was removed on purpose. */
  dismissed_at?: string | null;
}

export interface OperatingHoursSettings {
  timezone: string;
  operating_hours_enabled: boolean;
  hours: OperatingHoursDay[];
  closures: Closure[];
  /** Generated holidays removed on purpose. They can be put back. */
  dismissed: Closure[];
}

export const managementCompanyApi = {
  ...createApiWrapper<ManagementCompany>('management-companies'),

  getOperatingHours: async (id: number) =>
    apiRequest<OperatingHoursSettings>(
      `${SUPABASE_URL}/management-companies/${id}/operating-hours`
    ),

  saveOperatingHours: async (
    id: number,
    payload: Pick<OperatingHoursSettings, 'timezone' | 'operating_hours_enabled' | 'hours'>
  ) =>
    apiRequest<{ success: boolean }>(
      `${SUPABASE_URL}/management-companies/${id}/operating-hours`,
      { method: 'PUT', body: payload }
    ),

  addClosure: async (id: number, closure: Closure) =>
    apiRequest<Closure>(`${SUPABASE_URL}/management-companies/${id}/closures`, {
      method: 'POST',
      body: closure,
    }),

  /**
   * Removes a closure. A generated holiday is only marked, so regenerating the
   * calendar does not bring it back; a manual entry is deleted.
   */
  deleteClosure: async (id: number, closureId: number) =>
    apiRequest<{ success: boolean; dismissed: boolean }>(
      `${SUPABASE_URL}/management-companies/${id}/closures/${closureId}`,
      { method: 'DELETE' }
    ),

  restoreClosure: async (id: number, closureId: number) =>
    apiRequest<{ success: boolean }>(
      `${SUPABASE_URL}/management-companies/${id}/closures/${closureId}/restore`,
      { method: 'POST' }
    ),

  /**
   * Writes the US federal holidays as concrete dates for the next N years —
   * plus one state's own set when `state` is given ("cada estado tiene sus
   * propios feriados").
   */
  generateHolidays: async (
    id: number,
    options: { years?: number; observe_weekend_shift?: boolean; state?: string } = {}
  ) =>
    apiRequest<{ success: boolean; generated: number; skipped: number; years: number }>(
      `${SUPABASE_URL}/management-companies/${id}/closures/generate`,
      { method: 'POST', body: options }
    ),
};

// Permissions API
export const permissionApi = {
  list: async (page: number = 1, limit: number = 10) => 
    apiRequest<{ data: Permission[], metadata: { total_count: number } }>(`${SUPABASE_URL}/permissions?page=${page}&limit=${limit}`),
  getById: async (id: number) => 
    apiRequest<Permission>(`${SUPABASE_URL}/permissions/${id}`)
};

// RolePermission join API (assign and remove permissions from roles)
export const rolePermissionApi = {
  list: async (roleId: number) =>
    apiRequest<Permission[]>(`${SUPABASE_URL}/roles/${roleId}/permissions`),
  assign: async (roleId: number, permissionIds: number[]) =>
    apiRequest<void>(`${SUPABASE_URL}/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: { permission_ids: permissionIds }
    }),
};

// Role API with extended functionality
export const roleApi = {
  ...createApiWrapper<Role>('roles'),
  
  // Add methods for managing permissions
  getPermissions: async (roleId: number) =>
    apiRequest<number[]>(`${SUPABASE_URL}/roles/${roleId}/permissions`),
    
  getAllPermissions: async () =>
    apiRequest<Permission[]>(`${SUPABASE_URL}/permissions`),
    
  updatePermissions: async (roleId: number, permissionIds: number[]) =>
    apiRequest<void>(`${SUPABASE_URL}/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: { permission_ids: permissionIds }
    }),
  
  // Add methods for managing role assignments
  getPlatformRoles: async (userId: number) =>
    apiRequest<PlatformRoleAssignment[]>(`${SUPABASE_URL}/users/${userId}/platform-roles`),
    
  getManagementCompanyRoles: async (userId: number, managementCompanyId?: number) =>
    apiRequest<ManagementCompanyRoleAssignment[]>(
      `${SUPABASE_URL}/users/${userId}/management-company-roles${managementCompanyId ? `?management_company_id=${managementCompanyId}` : ''}`
    ),
    
  getAssociationRoles: async (userId: number, associationId?: number) =>
    apiRequest<AssociationRoleAssignment[]>(
      `${SUPABASE_URL}/users/${userId}/association-roles${associationId ? `?association_id=${associationId}` : ''}`
    ),
    
  assignPlatformRole: async (userId: number, roleId: number) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/platform-roles`, {
      method: 'POST',
      body: { role_id: roleId }
    }),
    
  assignManagementCompanyRole: async (userId: number, managementCompanyId: number, roleId: number) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/management-company-roles`, {
      method: 'POST',
      body: { role_id: roleId, management_company_id: managementCompanyId }
    }),
    
  assignAssociationRole: async (userId: number, associationId: number, roleId: number) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/association-roles`, {
      method: 'POST',
      body: { role_id: roleId, association_id: associationId }
    }),
    
  // Add legacy support for older components still using the setRoles method
  setRoles: async (userId: number, roleIds: number[]) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/platform-roles/batch`, {
      method: 'PUT',
      body: { role_ids: roleIds }
    })
};

// User API with custom methods
export const userApi = {
  ...createApiWrapper<User>('users'),

  // Custom methods
  verifyEmail: async (token: string) => 
    apiRequest<void>(`${SUPABASE_URL}/users/verify-email`, {
      method: 'POST',
      body: { token }
    }),
  
  login: async (email: string, password: string) => 
    apiRequest<{ user: User; token: string }>(`${SUPABASE_URL}/users/login`, {
      method: 'POST',
      body: { email, password }
    }),

  requestPasswordReset: async (email: string) => 
    apiRequest<void>(`${SUPABASE_URL}/users/request-password-reset`, {
      method: 'POST',
      body: { email }
    }),
  
  resetPassword: async (token: string, newPassword: string) => 
    apiRequest<void>(`${SUPABASE_URL}/users/reset-password`, {
      method: 'POST',
      body: { token, newPassword }
    }),

  // Assign or update roles for a user (replace all)
  setRoles: async (userId: number, roleIds: number[]) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/roles`, {
      method: 'PUT',
      body: { roles: roleIds }
    }),
  
  setAccessAllAssociations: async (userId: number, value: boolean) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/access-all-associations`, {
      method: 'PUT',
      body: { access_all_associations: value }
    }),

  setAssociations: async (userId: number, associationIds: number[]) =>
    apiRequest<void>(`${SUPABASE_URL}/users/${userId}/associations`, {
      method: 'PUT',
      body: { association_ids: associationIds }
    }),

  // Returns an array of Role objects for a user
  getUserRoles: async (userId: number) => 
    apiRequest<Role[]>(`${SUPABASE_URL}/users/${userId}/roles`),

  // Returns an array of association IDs a user is linked to
  getUserAssociations: async (userId: number) =>
    apiRequest<number[]>(`${SUPABASE_URL}/users/${userId}/associations`),

  // Bulk update user info and role assignments
  bulkUpdate: async (
    userId: number, 
    userInfo?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      management_company_id?: number;
      access_all_associations?: boolean;
    },
    roleAssignments?: {
      platform: number[];
      management_company: { [mcId: string]: number[] };
      association: { [assocId: string]: number[] };
    }
  ) => {
    const functionUrl = `${SUPABASE_URL}/users`;
    
    return apiRequest<{ success: boolean }>(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        action: 'bulk-update-user',
        userId,
        userInfo,
        roleAssignments: roleAssignments || {
          platform: [],
          management_company: {},
          association: {}
        }
      }
    });
  },
};
